/**
 * 场景人格（Facet）管理 Tab
 * 展示某身份下所有场景的 Facet 卡片
 * 
 * 参考：API 文档 第 8 节 Facet 管理接口
 * v9.2 更新：支持从语料库添加语料到场景
 * v9.2.1 更新：使用 SSE 实时获取学习进度，不再轮询
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Sparkles,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Mic,
} from 'lucide-react'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { SCENE_TAG_CONFIG } from '@/modules/identity/types/facet'
import { useToast } from '@/shared/hooks/useToast'
import { TokenManager } from '@/shared/services/api'
import { FacetApiService, IdentityApiService } from '@/modules/identity/services'
import { getLearningLLMModels } from '@/modules/identity/services/identity-api'
import { FacetCard, FacetCardSkeleton, CorpusSelectDialog } from '@/modules/identity/components'
import type { FacetListItem, FacetListResponse, SceneTag } from '@/modules/identity/types/facet'
import { SCENE_TAGS } from '@/modules/identity/types/facet'
import type { FacetSSEProgressEvent, FacetSSECompleteEvent, FacetSSEErrorEvent } from '@/modules/identity/hooks'
import type { LearningLLMModelOption } from '@/modules/identity/types'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

// SSE 进度状态类型
interface SSEProgressState {
  progress: number
  stage: string
  estimatedTime?: string
}

interface FacetTabProps {
  identityId: string
  /** v9.2: 不再使用，保留向后兼容 */
  onNavigateToCorpus?: () => void
}

/**
 * Facet 管理 Tab
 */
export function FacetTab({ identityId, onNavigateToCorpus: _onNavigateToCorpus }: FacetTabProps) {
  const navigate = useNavigate()
  const { showSuccess, showError } = useToast()

  // 数据状态
  const [facetData, setFacetData] = useState<FacetListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 操作状态
  const [learningScene, setLearningScene] = useState<SceneTag | null>(null)
  const [publishingScene, setPublishingScene] = useState<SceneTag | null>(null)

  // v9.2.1 SSE 进度状态：Map<SceneTag, SSEProgressState>
  const [sseProgress, setSSEProgress] = useState<Map<SceneTag, SSEProgressState>>(new Map())
  const eventSourcesRef = useRef<Map<SceneTag, EventSource>>(new Map())

  // v9.2 新增：语料选择弹窗状态
  const [corpusSelectOpen, setCorpusSelectOpen] = useState(false)
  const [selectedSceneTag, setSelectedSceneTag] = useState<SceneTag | null>(null)
  // 当前场景已关联的语料 ID 列表（用于禁用已选项）
  const [existingCorpusIds, setExistingCorpusIds] = useState<string[]>([])

  // v9.2.2 新增：删除确认弹窗状态
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [sceneToDelete, setSceneToDelete] = useState<SceneTag | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingScene, setDeletingScene] = useState<SceneTag | null>(null)

  // v10.2 更新：发布对话框状态（使用已绑定的音色）
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [sceneToPublish, setSceneToPublish] = useState<SceneTag | null>(null)
  const [publishDescription, setPublishDescription] = useState('')
  const [publishVoiceBinding, setPublishVoiceBinding] = useState<FacetListItem['voice_binding']>(null)

  // v1.7 新增：学习弹窗状态（支持 LLM 模型选择）
  const [learnDialogOpen, setLearnDialogOpen] = useState(false)
  const [sceneToLearn, setSceneToLearn] = useState<SceneTag | null>(null)
  const [availableLLMModels, setAvailableLLMModels] = useState<LearningLLMModelOption[]>([])
  const [selectedLLMModel, setSelectedLLMModel] = useState<string>('')

  // v1.7: 加载 LLM 模型配置
  useEffect(() => {
    async function loadLLMModels() {
      try {
        const models = await getLearningLLMModels()
        if (models && models.length > 0) {
          setAvailableLLMModels(models)
          // 设置默认模型
          const defaultModel = models.find(m => m.is_default)
          if (defaultModel) {
            setSelectedLLMModel(defaultModel.model_id)
          }
        }
      } catch (error) {
        console.warn('Failed to load LLM models config:', error)
        // 使用硬编码默认值作为降级
        setAvailableLLMModels([
          { model_id: 'deepseek-chat', name: 'DeepSeek Chat', provider: 'deepseek', is_default: true },
        ])
        setSelectedLLMModel('deepseek-chat')
      }
    }
    loadLLMModels()
  }, [])

  // ==================== 数据加载 ====================
  /**
   * 加载 Facet 列表
   * @param silent 静默模式，不显示 loading 状态（用于自动刷新）
   */
  const loadFacets = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true)
    }
    setError(null)
    try {
      const response = await FacetApiService.listFacets(identityId)
      console.log('🎭 [Facet] API 原始响应:', response)
      
      // 防御性编程：处理不同的响应结构
      // ApiClient 返回 {success, data, meta}，实际数据在 data 字段中
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let responseData = response.data as any
      
      // 如果 responseData 还有 items 字段，说明结构正确
      // 如果没有，可能需要再取一层 data
      if (!responseData?.items && responseData?.data?.items) {
        console.log('🎭 [Facet] 检测到嵌套结构，取 data.data')
        responseData = responseData.data
      }
      
      console.log('🎭 [Facet] 解析后数据:', responseData)
      console.log('🎭 [Facet] items 数量:', responseData?.items?.length)
      console.log('🎭 [Facet] active items:', responseData?.items?.filter((f: FacetListItem) => f.status === 'active'))
      
      // 如果后端返回的是直接数组，转换为标准格式
      let items: FacetListItem[] = []
      if (Array.isArray(responseData)) {
        items = responseData
      } else if (responseData?.items) {
        items = responseData.items
      } else {
        console.error('🎭 [Facet] 无法解析响应数据结构:', responseData)
        setError('数据格式异常')
        return
      }

      // v10.2: 为已激活的 facet 加载绑定信息（后端 listFacets 不返回 voice_binding）
      const activeFacets = items.filter(f => f.status === 'active' || f.status === 'draft')
      console.log('🎭 [Facet] activeFacets for binding:', activeFacets.map(f => f.scene_tag))
      if (activeFacets.length > 0) {
        const bindingPromises = activeFacets.map(async (facet) => {
          try {
            const bindingRes = await IdentityApiService.getFacetVoiceBinding(identityId, facet.scene_tag)
            console.log(`🎭 [Facet] ${facet.scene_tag} binding response:`, bindingRes)
            // 处理可能的嵌套结构
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const resData = bindingRes.data as any
            const voiceBinding = resData?.voice_binding ?? resData?.data?.voice_binding ?? null
            console.log(`🎭 [Facet] ${facet.scene_tag} voice_binding:`, voiceBinding)
            return { scene_tag: facet.scene_tag, binding: voiceBinding }
          } catch (err) {
            // 404 或其他错误，表示未绑定
            console.log(`🎭 [Facet] ${facet.scene_tag} binding error:`, err)
            return { scene_tag: facet.scene_tag, binding: null }
          }
        })
        const bindings = await Promise.all(bindingPromises)
        console.log('🎭 [Facet] all bindings:', bindings)
        
        // 将绑定信息合并到 items 中
        items = items.map(facet => {
          const bindingInfo = bindings.find(b => b.scene_tag === facet.scene_tag)
          if (bindingInfo) {
            return { ...facet, voice_binding: bindingInfo.binding }
          }
          return facet
        })
        console.log('🎭 [Facet] items after binding merge:', items.filter(f => f.voice_binding))
      }

      const facetData = {
        items,
        summary: responseData?.summary || {
          total_scenes: SCENE_TAGS.length,
          active_count: items.filter((f: FacetListItem) => f.status === 'active').length,
          learning_count: items.filter((f: FacetListItem) => f.status === 'learning').length,
          draft_count: items.filter((f: FacetListItem) => f.status === 'draft').length,
          not_started_count: items.filter((f: FacetListItem) => f.status === 'not_started').length,
        },
      }
      setFacetData(facetData)
    } catch (err) {
      console.error('Failed to load facets:', err)
      if (!silent) {
        setError('无法加载场景人格列表')
      }
    } finally {
      if (!silent) {
        setLoading(false)
      }
    }
  }, [identityId])

  useEffect(() => {
    loadFacets()
  }, [loadFacets])

  // ==================== SSE 连接管理 ====================
  
  /**
   * 连接单个场景的 SSE 进度流
   */
  const connectSSE = useCallback((sceneTag: SceneTag) => {
    // 已连接则跳过
    if (eventSourcesRef.current.has(sceneTag)) {
      console.log(`🔗 [SSE] ${sceneTag} 已连接，跳过`)
      return
    }
    
    const token = TokenManager.getToken()
    if (!token) {
      console.error('🔗 [SSE] 未找到 Token')
      return
    }
    
    const baseUrl = import.meta.env.VITE_AGENT_ADMIN_API_URL || '/api/v1'
    const url = `${baseUrl}/identity/identities/${identityId}/facets/${sceneTag}/learn/stream?token=${encodeURIComponent(token)}`
    
    console.log(`🔗 [SSE] 连接 ${sceneTag}:`, url.substring(0, 80) + '...')
    
    const eventSource = new EventSource(url)
    eventSourcesRef.current.set(sceneTag, eventSource)
    
    // 初始化进度状态
    setSSEProgress(prev => {
      const next = new Map(prev)
      next.set(sceneTag, { progress: 0, stage: '连接中...' })
      return next
    })
    
    eventSource.onopen = () => {
      console.log(`✅ [SSE] ${sceneTag} 连接成功`)
    }
    
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log(`📨 [SSE] ${sceneTag}:`, data.type, data)
        
        if (data.type === 'progress') {
          const progressEvent = data as FacetSSEProgressEvent
          setSSEProgress(prev => {
            const next = new Map(prev)
            next.set(sceneTag, {
              progress: progressEvent.progress,
              stage: progressEvent.stage_description,
              estimatedTime: progressEvent.estimated_remaining_time,
            })
            return next
          })
        } else if (data.type === 'complete') {
          const completeEvent = data as FacetSSECompleteEvent
          console.log(`🎉 [SSE] ${sceneTag} 学习完成`, completeEvent.status)
          
          // 清理进度状态
          setSSEProgress(prev => {
            const next = new Map(prev)
            next.delete(sceneTag)
            return next
          })
          
          // 关闭连接
          eventSource.close()
          eventSourcesRef.current.delete(sceneTag)
          
          // 刷新 Facet 列表
          loadFacets(true)
          showSuccess(`${sceneTag} 学习完成！`, '学习成功')
          
        } else if (data.type === 'error') {
          const errorEvent = data as FacetSSEErrorEvent
          console.error(`❌ [SSE] ${sceneTag} 学习失败:`, errorEvent.error)
          
          // 清理进度状态
          setSSEProgress(prev => {
            const next = new Map(prev)
            next.delete(sceneTag)
            return next
          })
          
          // 关闭连接
          eventSource.close()
          eventSourcesRef.current.delete(sceneTag)
          
          // 刷新列表并显示错误
          loadFacets(true)
          showError(errorEvent.error || '学习失败', '学习错误')
        }
      } catch (err) {
        console.error(`❌ [SSE] ${sceneTag} 解析消息失败:`, err)
      }
    }
    
    eventSource.onerror = (err) => {
      console.error(`❌ [SSE] ${sceneTag} 连接错误:`, err)
      
      // 关闭并清理
      eventSource.close()
      eventSourcesRef.current.delete(sceneTag)
      
      // 清理进度状态
      setSSEProgress(prev => {
        const next = new Map(prev)
        next.delete(sceneTag)
        return next
      })
    }
  }, [identityId, loadFacets, showSuccess, showError])
  
  /**
   * 断开单个场景的 SSE 连接
   */
  const disconnectSSE = useCallback((sceneTag: SceneTag) => {
    const eventSource = eventSourcesRef.current.get(sceneTag)
    if (eventSource) {
      console.log(`🔌 [SSE] 断开 ${sceneTag}`)
      eventSource.close()
      eventSourcesRef.current.delete(sceneTag)
      
      setSSEProgress(prev => {
        const next = new Map(prev)
        next.delete(sceneTag)
        return next
      })
    }
  }, [])
  
  /**
   * 断开所有 SSE 连接
   */
  const disconnectAllSSE = useCallback(() => {
    eventSourcesRef.current.forEach((es, tag) => {
      console.log(`🔌 [SSE] 断开 ${tag}`)
      es.close()
    })
    eventSourcesRef.current.clear()
    setSSEProgress(new Map())
  }, [])
  
  // 页面卸载时清理所有 SSE 连接
  useEffect(() => {
    return () => {
      disconnectAllSSE()
    }
  }, [disconnectAllSSE])
  
  // 当 Facet 数据加载后，检查是否有学习中的场景，自动连接 SSE
  useEffect(() => {
    const learningFacets = facetData?.items?.filter(f => f.status === 'learning') || []
    
    learningFacets.forEach(facet => {
      if (!eventSourcesRef.current.has(facet.scene_tag)) {
        connectSSE(facet.scene_tag)
      }
    })
    
    // 清理不再学习的场景的 SSE 连接
    eventSourcesRef.current.forEach((_, tag) => {
      const facet = facetData?.items?.find(f => f.scene_tag === tag)
      if (!facet || facet.status !== 'learning') {
        disconnectSSE(tag)
      }
    })
  }, [facetData?.items, connectSSE, disconnectSSE])

  // ==================== 操作处理 ====================

  /**
   * 查看 Facet 详情
   */
  const handleViewFacet = useCallback((sceneTag: SceneTag) => {
    navigate(`/identities/${identityId}/facets/${sceneTag}`)
  }, [navigate, identityId])

  /**
   * v1.7: 打开学习对话框（支持 LLM 模型选择）
   */
  const handleLearnClick = useCallback((sceneTag: SceneTag) => {
    setSceneToLearn(sceneTag)
    setLearnDialogOpen(true)
  }, [])

  /**
   * v1.7: 确认开始学习（使用选择的 LLM 模型）
   */
  const handleConfirmLearn = useCallback(async () => {
    if (!sceneToLearn) return
    
    setLearningScene(sceneToLearn)
    setLearnDialogOpen(false)
    
    try {
      // v1.7: 传递选择的 LLM 模型
      await FacetApiService.learnFacet(identityId, sceneToLearn, {
        model: selectedLLMModel || undefined,
      })
      showSuccess('学习任务已启动，正在连接进度流...', '开始学习')
      
      // 刷新列表（状态会变为 learning）
      await loadFacets(true)
      
      // 立即连接 SSE 获取实时进度
      connectSSE(sceneToLearn)
      
    } catch (err) {
      console.error('Failed to start learning:', err)
      showError('启动学习失败', '操作失败')
    } finally {
      setLearningScene(null)
      setSceneToLearn(null)
    }
  }, [identityId, sceneToLearn, selectedLLMModel, showSuccess, showError, loadFacets, connectSSE])

  /**
   * v10.2: 打开发布对话框（使用已绑定的音色，不需要选择）
   */
  const handlePublishClick = useCallback((sceneTag: SceneTag) => {
    // 获取该 facet 的绑定信息
    const facet = facetData?.items?.find(f => f.scene_tag === sceneTag)
    
    setSceneToPublish(sceneTag)
    setPublishDescription('')
    setPublishVoiceBinding(facet?.voice_binding || null)
    setPublishDialogOpen(true)
  }, [facetData])

  /**
   * v10.2: 确认发布 Facet（使用已绑定的音色）
   * v9.2.2: 发布成功后自动创建 Identity 版本
   */
  const handleConfirmPublish = useCallback(async () => {
    if (!sceneToPublish) return
    
    // v10.2: 检查是否已绑定音色
    if (!publishVoiceBinding) {
      showError('请先在卡片详情页绑定音色', '验证失败')
      return
    }
    
    setPublishingScene(sceneToPublish)
    try {
      // 1. 发布 Facet（v10.2: 使用已绑定的音色卡片 ID）
      const publishResult = await FacetApiService.publishFacet(identityId, sceneToPublish, {
        voice_id: publishVoiceBinding.voice_card_id,
        description: publishDescription || undefined,
      })
      const facetVersion = publishResult.data?.version || 'unknown'
      const sceneName = SCENE_TAG_CONFIG[sceneToPublish]?.label || sceneToPublish
      
      // 2. 自动创建 Identity 版本（记录此次发布）
      try {
        await IdentityApiService.publishVersion(identityId, {
          description: `发布场景「${sceneName}」(v${facetVersion})`,
        })
        showSuccess(`「${sceneName}」已发布，版本已记录`, '发布成功')
      } catch (versionErr) {
        console.warn('Failed to create identity version:', versionErr)
        // 版本创建失败不影响 Facet 发布成功
        showSuccess(`「${sceneName}」已发布`, '发布成功')
      }
      
      setPublishDialogOpen(false)
      setSceneToPublish(null)
      loadFacets() // 刷新列表
    } catch (err) {
      console.error('Failed to publish facet:', err)
      showError('发布失败', '操作失败')
    } finally {
      setPublishingScene(null)
    }
  }, [identityId, sceneToPublish, publishVoiceBinding, publishDescription, showSuccess, showError, loadFacets])

  /**
   * v9.2: 打开语料选择弹窗
   */
  const handleAddCorpus = useCallback((sceneTag: SceneTag) => {
    // 获取该场景已关联的语料 ID
    const facet = facetData?.items?.find(f => f.scene_tag === sceneTag)
    const existingIds = facet?.corpus_list?.map(c => c.corpus_id) || []
    
    setSelectedSceneTag(sceneTag)
    setExistingCorpusIds(existingIds)
    setCorpusSelectOpen(true)
  }, [facetData])

  /**
   * v9.2: 语料添加成功后刷新
   */
  const handleCorpusAddSuccess = useCallback(() => {
    loadFacets() // 刷新 Facet 列表以更新 corpus_list
  }, [loadFacets])

  /**
   * v9.2.2: 打开删除确认弹窗
   */
  const handleDeleteClick = useCallback((sceneTag: SceneTag) => {
    setSceneToDelete(sceneTag)
    setDeleteConfirmText('')
    setDeleteDialogOpen(true)
  }, [])

  /**
   * v9.2.2: 确认删除（重置 Facet）
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!sceneToDelete || deleteConfirmText !== '删除') return

    setDeletingScene(sceneToDelete)
    try {
      await FacetApiService.resetFacet(identityId, sceneToDelete)
      showSuccess('场景已重置为未学习状态', '删除成功')
      setDeleteDialogOpen(false)
      loadFacets() // 刷新列表
    } catch (err) {
      console.error('Failed to reset facet:', err)
      showError('删除失败', '操作失败')
    } finally {
      setDeletingScene(null)
      setSceneToDelete(null)
    }
  }, [sceneToDelete, deleteConfirmText, identityId, showSuccess, showError, loadFacets])

  // ==================== 分组数据 ====================
  const items = facetData?.items || []
  const learnedFacets = items.filter(f => 
    f.status === 'active' || f.status === 'learning' || f.status === 'draft' || f.status === 'failed'
  )
  const notStartedFacets = items.filter(f => f.status === 'not_started')

  // ==================== 渲染 ====================
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-500" />
              场景人格管理
            </CardTitle>
            <CardDescription>
              同一个人在不同场景下的说话方式（Facet）
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadFacets()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 总览统计 */}
        {facetData?.summary && (
          <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
            <Badge variant="outline" className="text-sm">
              📊 总览
            </Badge>
            <span className="text-sm text-slate-600">
              {facetData.summary.total_scenes} 个场景
            </span>
            <span className="text-sm text-slate-600">·</span>
            <span className="text-sm text-emerald-600">
              {facetData.summary.active_count} 个已学习
            </span>
            <span className="text-sm text-slate-600">·</span>
            <span className="text-sm text-slate-400">
              {facetData.summary.not_started_count} 个未学习
            </span>
          </div>
        )}

        {/* 加载状态 */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <FacetCardSkeleton key={i} />
            ))}
          </div>
        )}

        {/* 错误状态 */}
        {error && !loading && (
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
            <p className="text-slate-600 mb-4">{error}</p>
            <Button variant="outline" onClick={() => loadFacets()}>
              重试
            </Button>
          </div>
        )}

        {/* 数据展示 */}
        {!loading && !error && facetData && (
          <>
            {/* 已学习的场景 */}
            {learnedFacets.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <div className="w-1 h-4 bg-violet-500 rounded" />
                  已学习的场景
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {learnedFacets.map(facet => {
                    const progress = sseProgress.get(facet.scene_tag)
                    return (
                      <FacetCard
                        key={facet.scene_tag}
                        facet={facet}
                        onView={() => handleViewFacet(facet.scene_tag)}
                        onLearn={() => handleLearnClick(facet.scene_tag)}
                        onPublish={() => handlePublishClick(facet.scene_tag)}
                        onAddCorpus={() => handleAddCorpus(facet.scene_tag)}
                        onDelete={() => handleDeleteClick(facet.scene_tag)}
                        isLearning={learningScene === facet.scene_tag}
                        isPublishing={publishingScene === facet.scene_tag}
                        isDeleting={deletingScene === facet.scene_tag}
                        // SSE 实时进度
                        sseProgress={progress?.progress}
                        sseStage={progress?.stage}
                        sseEstimatedTime={progress?.estimatedTime}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {/* 未学习的场景 */}
            {notStartedFacets.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <div className="w-1 h-4 bg-slate-300 rounded" />
                  未学习的场景
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {notStartedFacets.map(facet => {
                    const progress = sseProgress.get(facet.scene_tag)
                    return (
                      <FacetCard
                        key={facet.scene_tag}
                        facet={facet}
                        onAddCorpus={() => handleAddCorpus(facet.scene_tag)}
                        onLearn={() => handleLearnClick(facet.scene_tag)}
                        isLearning={learningScene === facet.scene_tag}
                        // SSE 实时进度
                        sseProgress={progress?.progress}
                        sseStage={progress?.stage}
                        sseEstimatedTime={progress?.estimatedTime}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {/* 空状态 */}
            {items.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>暂无场景数据</p>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* v9.2 新增：语料选择弹窗 */}
      {selectedSceneTag && (
        <CorpusSelectDialog
          open={corpusSelectOpen}
          onOpenChange={setCorpusSelectOpen}
          identityId={identityId}
          sceneTag={selectedSceneTag}
          existingCorpusIds={existingCorpusIds}
          onSuccess={handleCorpusAddSuccess}
        />
      )}

      {/* v9.2.2 新增：删除确认弹窗 */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              确认删除场景
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <p>
                您确定要删除「<strong>{sceneToDelete ? SCENE_TAG_CONFIG[sceneToDelete]?.label : ''}</strong>」的学习结果吗？
              </p>
              <p className="text-amber-600">
                ⚠️ 删除后该场景将回归到「未学习」状态，所有学习产出物（Injection Pack）将被清除。
              </p>
              <p>
                请在下方输入框中输入「<strong className="text-red-600">删除</strong>」以确认操作。
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="请输入「删除」"
              className="text-center"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={!!deletingScene}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteConfirmText !== '删除' || !!deletingScene}
            >
              {deletingScene ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  删除中...
                </>
              ) : (
                '确认删除'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v10.2 更新：发布对话框（使用已绑定的音色）*/}
      <Dialog open={publishDialogOpen} onOpenChange={(open) => {
        setPublishDialogOpen(open)
        if (!open) {
          setSceneToPublish(null)
          setPublishDescription('')
          setPublishVoiceBinding(null)
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-500" />
              发布场景「{sceneToPublish ? SCENE_TAG_CONFIG[sceneToPublish]?.label : ''}」
            </DialogTitle>
            <DialogDescription>
              发布后该场景人格将可用于 Agent Flow 中的身份模拟。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* v10.2: 显示已绑定的音色信息 */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Mic className="h-4 w-4" />
                绑定音色
              </Label>
              {publishVoiceBinding ? (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <Mic className="h-4 w-4 text-violet-500" />
                    {publishVoiceBinding.voice_card_name}
                  </p>
                  <p className="text-xs text-slate-500 ml-6 mt-1">
                    模型: {publishVoiceBinding.model_id?.substring(0, 16)}... |
                    语速 {publishVoiceBinding.speech_rate?.toFixed(1) || '1.0'} |
                    音调 {publishVoiceBinding.pitch_rate?.toFixed(1) || '1.0'} |
                    音量 {publishVoiceBinding.volume || 50}
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    未绑定音色
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    请先点击卡片进入详情页，在「音色」Tab 绑定一个已发布的音色。
                  </p>
                </div>
              )}
            </div>

            {/* 版本说明（可选）*/}
            <div className="space-y-2">
              <Label>版本说明（可选）</Label>
              <Textarea
                value={publishDescription}
                onChange={(e) => setPublishDescription(e.target.value)}
                placeholder="描述本次发布的更新内容..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPublishDialogOpen(false)}
              disabled={!!publishingScene}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmPublish}
              disabled={!publishVoiceBinding || !!publishingScene}
            >
              {publishingScene ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  发布中...
                </>
              ) : (
                '确认发布'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v1.7 新增：学习对话框（LLM 模型选择）*/}
      <Dialog open={learnDialogOpen} onOpenChange={(open) => {
        setLearnDialogOpen(open)
        if (!open) {
          setSceneToLearn(null)
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-violet-500" />
              开始学习「{sceneToLearn ? SCENE_TAG_CONFIG[sceneToLearn]?.label : ''}」
            </DialogTitle>
            <DialogDescription>
              选择 LLM 模型进行场景人格学习。不同模型在理解和生成能力上有所差异。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* LLM 模型选择 */}
            <div className="space-y-2">
              <Label>LLM 模型</Label>
              {availableLLMModels.length > 0 ? (
                <Select
                  value={selectedLLMModel}
                  onValueChange={setSelectedLLMModel}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择 LLM 模型" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLLMModels.map((model) => (
                      <SelectItem key={model.model_id} value={model.model_id}>
                        <div className="flex flex-col">
                          <span>{model.name}</span>
                          {model.description && (
                            <span className="text-xs text-slate-500">{model.description}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-slate-500">正在加载模型列表...</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setLearnDialogOpen(false)}
              disabled={!!learningScene}
            >
              取消
            </Button>
            <Button
              onClick={handleConfirmLearn}
              disabled={!selectedLLMModel || !!learningScene}
            >
              {learningScene ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  启动中...
                </>
              ) : (
                '开始学习'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
